/**
 * DrayageResourceManager - Singleton class for managing Drayage app resources
 * Loads resources once at app startup and caches them for optimal performance
 */
class DrayageResourceManager {
  constructor() {
    this.isInitialized = false;
    this.isLoading = false;
    this.resources = [];
    this.loadPromise = null;
    this.assets = null;
    this.error = null;
    this.cachedAssetUrls = null;
    
    // App URL based on environment variable
    this.DRAYAGE_URL = process.env.DRAYAGE_ASSETS || 'http://localhost:3001';
  }

  /**
   * Get the singleton instance
   */
  static getInstance() {
    if (!DrayageResourceManager.instance) {
      DrayageResourceManager.instance = new DrayageResourceManager();
    }
    return DrayageResourceManager.instance;
  }

  /**
   * Load manifest and get all asset URLs dynamically
   */
  async loadManifestAndGetAssets() {
    try {
      const manifestResponse = await fetch(`${this.DRAYAGE_URL}/manifest.json`, {
        cache: 'no-store',
      });
      const manifest = await manifestResponse.json();

      const assets = {
        jsFiles: [],
        cssFiles: []
      };

      // Process all entries in the manifest
      Object.entries(manifest).forEach(([key, entry]) => {
        // Add JS files (look for .file property which contains the main JS)
        if (entry.file && entry.file.endsWith('.js')) {
          assets.jsFiles.push(`${this.DRAYAGE_URL}/${entry.file}`);
        }

        // Add CSS files - can be in multiple places:
        // 1. Direct file entries where file ends with .css (component CSS)
        // 2. In the css array of each entry (referenced CSS)
        // 3. CSS source entries (have src property pointing to the original CSS file)
        if (entry.file && entry.file.endsWith('.css')) {
          const cssUrl = `${this.DRAYAGE_URL}/${entry.file}`;
          if (!assets.cssFiles.includes(cssUrl)) {
            assets.cssFiles.push(cssUrl);
          }
        }

        // Also add CSS source files (entries where src points to a CSS file)
        // These are CSS source entries that look like: { file: "DimensionField.css", src: "_DimensionField.xxx.js" }
        if (entry.file && entry.file.endsWith('.css') && entry.src) {
          const cssUrl = `${this.DRAYAGE_URL}/${entry.file}`;
          if (!assets.cssFiles.includes(cssUrl)) {
            assets.cssFiles.push(cssUrl);
          }
        }

        if (entry.css && Array.isArray(entry.css)) {
          entry.css.forEach(cssFile => {
            const cssUrl = `${this.DRAYAGE_URL}/${cssFile}`;
            if (!assets.cssFiles.includes(cssUrl)) {
              assets.cssFiles.push(cssUrl);
            }
          });
        }
      });

      // Validate that we found assets
      if (assets.jsFiles.length === 0 && assets.cssFiles.length === 0) {
        throw new Error("No assets found in manifest");
      }

      return assets;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Clean up existing resources
   */  
  cleanupExistingResources() {
    // Clean up previous scripts
    const scripts = document.querySelectorAll(`script[src*="${this.DRAYAGE_URL}"]`);
    scripts.forEach(script => {
      script.parentNode.removeChild(script);
    });

    // Clean up previous styles
    const styles = document.querySelectorAll(`link[href*="${this.DRAYAGE_URL}"]`);
    styles.forEach(style => {
      style.parentNode.removeChild(style);
    });

    // Only clear drayageApp; keep mountDrayage so remounts can reuse the loaded entrypoint
    if (window.drayageApp) {
      delete window.drayageApp;
    }

    // Clear resource references to allow GC of removed DOM nodes
    this.resources = [];
  }

  /**
   * Load all resources and cache them
   */
  async loadResources(isRetry = false) {
    if (this.isLoading && this.loadPromise) {
      return this.loadPromise;
    }

    if (this.isInitialized && !isRetry) {
      return Promise.resolve();
    }

    this.isLoading = true;
    this.error = null;

    this.loadPromise = new Promise(async (resolve, reject) => {
      try {
        // Always clean up first on retry
        if (isRetry) {
          this.cleanupExistingResources();
        }
        
        // Load manifest and get asset URLs — use cache if available
        if (this.cachedAssetUrls && !isRetry) {
          // console.log('[DrayageResourceManager] Using cached asset URLs — skipping manifest fetch');
          this.assets = this.cachedAssetUrls;
        } else {
          // console.log('[DrayageResourceManager] Fetching manifest from', this.DRAYAGE_URL);
          this.assets = await this.loadManifestAndGetAssets();
          this.cachedAssetUrls = this.assets;
        }
        
        // Keep track of loaded resources
        this.resources = [];
        let loadedCount = 0;
        const totalResources = this.assets.jsFiles.length + this.assets.cssFiles.length;

        // Function to check if all resources are loaded
        const checkAllResourcesLoaded = () => {
          loadedCount++;

          if (loadedCount === totalResources) {
            // Small delay to ensure CSS is fully parsed
            setTimeout(() => {
              this.isLoading = false;
              this.isInitialized = true;
              resolve();
            }, 100);
          }
        };

        // Set up error handling with timeout
        const timeoutId = setTimeout(() => {
          this.error = "Timeout: Unable to load Drayage application resources.";
          this.isLoading = false;
          reject(new Error("Loading timeout"));
        }, process.env.DRAYAGE_RESOURCE_TIMEOUT || 60000);

        // Load all CSS files dynamically
        this.assets.cssFiles.forEach((cssUrl) => {
          const cssLink = document.createElement("link");
          cssLink.rel = "stylesheet";
          cssLink.href = cssUrl;
          cssLink.onload = () => {
            checkAllResourcesLoaded();
          };
          cssLink.onerror = (e) => {
            clearTimeout(timeoutId);
            this.error = "Failed to load Drayage application styles";
            this.isLoading = false;
            reject(e);
          };
          document.head.appendChild(cssLink);
          this.resources.push({ element: cssLink, parent: document.head });
        });

        // Load all JavaScript files dynamically
        this.assets.jsFiles.forEach((jsUrl) => {
          const script = document.createElement("script");
          script.src = jsUrl;
          script.async = true;
          script.type = "module";
          this.resources.push({ element: script, parent: document.body });

          script.onload = () => {
            clearTimeout(timeoutId);
            checkAllResourcesLoaded();
          };

          script.onerror = e => {
            clearTimeout(timeoutId);
            this.error = "Failed to load Drayage application script";
            this.isLoading = false;
            reject(e);
          };

          document.body.appendChild(script);
        });

      } catch (error) {
        this.error = error.message;
        this.isLoading = false;
        reject(error);
      }
    });

    return this.loadPromise;
  }

  /**
   * Retry loading resources
   */
  async retryLoadResources() {
    this.isInitialized = false;
    this.loadPromise = null;
    return this.loadResources(true);
  }

  /**
   * Clear cache and reset loading state
   */
  async clearCache() {
    this.isInitialized = false;
    this.isLoading = false;
    this.loadPromise = null;
    this.assets = null;
    this.error = null;
    this.cachedAssetUrls = null;

    // Clean up existing resources
    this.cleanupExistingResources();
  }

  /**
   * Remove drayage CSS/JS elements from the DOM without clearing the cache.
   * Used when navigating away from drayage screens to prevent CSS conflicts.
   */
  detachResources() {
    const scripts = document.querySelectorAll(`script[src*="${this.DRAYAGE_URL}"]`);
    scripts.forEach(script => {
      script.parentNode.removeChild(script);
    });

    const styles = document.querySelectorAll(`link[href*="${this.DRAYAGE_URL}"]`);
    styles.forEach(style => {
      style.parentNode.removeChild(style);
    });

    if (window.drayageApp) {
      delete window.drayageApp;
    }

    // Clear resource references to allow GC of removed DOM nodes
    this.resources = [];
    this.isInitialized = false;
  }

  /**
   * Check if resources are ready
   */
  isReady() {
    return this.isInitialized && !this.isLoading && window.mountDrayage;
  }

  /**
   * Get current error state
   */
  getError() {
    return this.error;
  }

  /**
   * Clean up all resources (for app shutdown)
   */
  cleanup() {
    this.detachResources();

    this.resources = [];
    this.loadPromise = null;
    this.assets = null;
    this.error = null;
    this.cachedAssetUrls = null;
    this.isLoading = false;
    this.isInitialized = false;
  }
}

export default DrayageResourceManager;
